import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { StyleSelect } from "../components/StyleSelect";
import { toast } from "sonner";
import { Plus, Trash2, X, Sparkles, Check, FolderPlus, Lightbulb, LayoutTemplate } from "lucide-react";

const CATEGORIES = ["apparel", "accessories", "shoes"];
const SOURCES = [
  { key: "auto", label: "Auto from Libraries", hint: "AI picks a suitable library material per product based on your applicability tags" },
  { key: "pool", label: "From a chosen pool", hint: "AI only uses the materials you select below" },
  { key: "prompt", label: "From the description", hint: "Render straight from your text — no specific swatch applied" },
];

function MaterialPool({ value, onChange }) {
  const [materials, setMaterials] = useState([]);
  useEffect(() => { api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {}); }, []);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="mt-2 grid grid-cols-5 gap-2 max-h-52 overflow-y-auto pr-1" data-testid="collection-pool-grid">
      {materials.length === 0 && <p className="col-span-5 text-[11px] text-zinc-400 border border-dashed border-zinc-200 rounded-md p-3">No materials available yet.</p>}
      {materials.map((m) => (
        <button key={m.id} type="button" data-testid={`collection-pool-${m.id}`} onClick={() => toggle(m.id)} title={m.name}
          className={`relative border rounded-md overflow-hidden transition-colors ${value.includes(m.id) ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
          {m.source === "plm" && <span className="absolute top-1 left-1 bg-zinc-950 text-white text-[8px] uppercase tracking-[0.1em] px-1 py-0.5 rounded z-10">PLM</span>}
          {value.includes(m.id) && <span className="absolute top-1 right-1 bg-emerald-500 text-white rounded-full p-0.5 z-10"><Check size={10} /></span>}
          <img src={imgUrl(m.image_url)} alt={m.name} className="w-full h-14 object-cover" />
          <p className="text-[9px] p-1 truncate">{m.name}</p>
        </button>
      ))}
    </div>
  );
}

function AddToCollectionModal({ count, onClose, onDone }) {
  const [collections, setCollections] = useState([]);
  const [mode, setMode] = useState("existing"); // existing | new
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState({ name: "", season: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/collections").then((r) => {
      const cols = r.data.filter((c) => c.kind !== "ideas");
      setCollections(cols);
      if (cols[0]) setSelectedId(cols[0].id);
      else setMode("new");
    }).catch(() => {});
  }, []);

  const confirm = async () => {
    setBusy(true);
    try {
      let colId = selectedId;
      if (mode === "new") {
        if (!form.name.trim()) { toast.error("Name the collection"); setBusy(false); return; }
        const { data } = await api.post("/collections", { name: form.name.trim(), kind: form.season.trim() ? "season" : "general", season: form.season.trim() });
        colId = data.id;
      }
      if (!colId) { toast.error("Pick a collection"); setBusy(false); return; }
      await onDone(colId);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-zinc-950/60 flex items-center justify-center p-4" data-testid="collection-add-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Add {count} design{count !== 1 ? "s" : ""} to a collection</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            <button data-testid="collection-add-mode-existing" onClick={() => setMode("existing")} disabled={collections.length === 0}
              className={`px-3 py-1.5 rounded-md text-sm border transition-colors disabled:opacity-40 ${mode === "existing" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>Existing</button>
            <button data-testid="collection-add-mode-new" onClick={() => setMode("new")}
              className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${mode === "new" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>New collection</button>
          </div>
          {mode === "existing" ? (
            <select data-testid="collection-add-select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
              className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              {collections.map((c) => <option key={c.id} value={c.id}>{c.name}{c.season ? ` · ${c.season}` : ""}</option>)}
            </select>
          ) : (
            <div className="space-y-3">
              <input data-testid="collection-add-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Collection name — e.g. SS26 Resort"
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input data-testid="collection-add-season" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="Season (optional) — e.g. Spring/Summer 2026"
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            </div>
          )}
          <button data-testid="collection-add-confirm" onClick={confirm} disabled={busy}
            className="w-full bg-zinc-950 text-white py-2.5 rounded-md font-medium hover:bg-zinc-800 disabled:opacity-40">
            {busy ? "Saving…" : "Save to collection"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CollectionStudio() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([{ product_type: "", count: 1, description: "" }]);
  const [category, setCategory] = useState("apparel");
  const [gender, setGender] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [examples, setExamples] = useState(1);
  const [materialSource, setMaterialSource] = useState("auto");
  const [pool, setPool] = useState([]);
  const [styleId, setStyleId] = useState("");
  const [budget, setBudget] = useState(2);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [brief, setBrief] = useState("");
  const [seededBoard, setSeededBoard] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api.get("/generate/collection/config").then((r) => {
      setConfig(r.data);
      setProducts([{ product_type: r.data.product_types[0] || "", count: 1, description: "" }]);
    }).catch(() => {});
  }, []);

  // Seed from a moodboard: compose a brief from its theme, color story & keywords
  useEffect(() => {
    const boardId = new URLSearchParams(location.search).get("board");
    if (!boardId) return;
    api.get(`/moodboards/${boardId}`).then((r) => {
      const b = r.data;
      const parts = [];
      if (b.theme) parts.push(b.theme);
      const palette = (b.items || []).filter((i) => i.type === "color").map((i) => `${i.label || ""} ${i.color}`.trim());
      if (palette.length) parts.push(`Color story: ${palette.join(", ")}`);
      const texts = (b.items || []).filter((i) => i.type === "text" && i.text).map((i) => i.text.trim());
      if (texts.length) parts.push(`Mood keywords: ${texts.join(" / ")}`);
      setBrief(parts.join(". "));
      setSeededBoard({ id: b.id, name: b.name });
      if (b.theme) toast.success(`Seeded from moodboard "${b.name}"`);
    }).catch(() => {});
  }, [location.search]);

  const costPer = config?.cost_per_render_usd ?? 0.04;
  const maxRenders = config?.max_renders_per_run ?? 12;

  const planned = useMemo(
    () => products.reduce((s, p) => s + (Number(p.count) || 0), 0) * (Number(examples) || 1),
    [products, examples]
  );
  const willGenerate = Math.min(planned, Math.floor((Number(budget) || 0) / costPer), maxRenders);
  const estCost = (willGenerate * costPer).toFixed(2);
  const skipped = Math.max(0, planned - willGenerate);

  const setProduct = (i, k, v) => setProducts(products.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const addProduct = () => setProducts([...products, { product_type: config?.product_types?.[0] || "", count: 1, description: "" }]);
  const removeProduct = (i) => setProducts(products.filter((_, idx) => idx !== i));

  const canGenerate = products.some((p) => p.product_type && Number(p.count) > 0) && willGenerate > 0 && !generating;

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    setSelected({});
    try {
      const { data } = await api.post("/generate/collection", {
        products: products.filter((p) => p.product_type && Number(p.count) > 0).map((p) => ({ product_type: p.product_type, count: Number(p.count), description: p.description })),
        examples_per_product: Number(examples) || 1,
        category, gender: gender || null, age_group: ageGroup || null,
        style_id: styleId || null, material_source: materialSource,
        material_pool: materialSource === "pool" ? pool : [],
        budget_usd: Number(budget) || 0,
        brief: brief || "",
      });
      setResult(data);
      if (data.generated === 0) toast.error("No renders were produced — try again");
      else toast.success(`Generated ${data.generated} concept${data.generated !== 1 ? "s" : ""}${data.skipped ? ` · ${data.skipped} skipped by budget` : ""}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  const designs = result?.designs || [];
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const toggleSel = (id) => setSelected({ ...selected, [id]: !selected[id] });
  const allSelected = designs.length > 0 && selectedIds.length === designs.length;
  const toggleAll = () => setSelected(allSelected ? {} : designs.reduce((a, d) => (a[d.id] = true, a), {}));

  const discard = async (id) => {
    try {
      await api.delete(`/designs/${id}`);
      setResult({ ...result, designs: designs.filter((d) => d.id !== id) });
      const s = { ...selected }; delete s[id]; setSelected(s);
      toast.success("Discarded");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveToIdeas = async () => {
    if (selectedIds.length === 0) { toast.error("Select some designs first"); return; }
    try {
      const { data } = await api.post("/collections/ideas");
      await Promise.all(selectedIds.map((id) => api.post(`/collections/${data.id}/designs`, { design_id: id })));
      toast.success(`Saved ${selectedIds.length} to Ideas`);
      setSelected({});
    } catch (e) { toast.error(apiError(e)); }
  };

  const addToCollection = async (colId) => {
    await Promise.all(selectedIds.map((id) => api.post(`/collections/${colId}/designs`, { design_id: id })));
    toast.success(`Added ${selectedIds.length} to collection`);
    setShowAdd(false);
    setSelected({});
    navigate(`/collections/${colId}`);
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">AI Studio</p>
        <h1 className="font-heading text-4xl font-bold mt-1 tracking-tight" data-testid="collection-studio-title">Design a Collection</h1>
        <p className="text-zinc-500 mt-2 text-sm max-w-2xl">Describe the products you want, how many of each and your budget — the AI drafts a whole collection, pulling suitable materials from your Libraries. Review, keep the ones you love and push them into your workflow.</p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* ---- Config form ---- */}
        <div className="lg:col-span-2 space-y-6">
          {seededBoard && (
            <div className="flex items-center gap-2 bg-zinc-950 text-white rounded-lg px-3 py-2.5 text-sm" data-testid="collection-seeded-banner">
              <LayoutTemplate size={15} className="shrink-0" />
              <span className="min-w-0 truncate">Seeded from moodboard <b>{seededBoard.name}</b></span>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Collection brief / mood <span className="text-zinc-400 normal-case tracking-normal">(optional)</span></label>
            <textarea data-testid="collection-brief" value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
              placeholder="Overall creative direction, color story and mood applied to every product — e.g. coastal minimalism, sun-bleached neutrals, relaxed tailoring"
              className="mt-2 w-full border border-zinc-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Products</label>
            <div className="mt-2 space-y-3">
              {products.map((p, i) => (
                <div key={i} className="border border-zinc-200 rounded-lg p-3 bg-white" data-testid={`collection-product-${i}`}>
                  <div className="flex gap-2">
                    <select data-testid={`collection-product-type-${i}`} value={p.product_type} onChange={(e) => setProduct(i, "product_type", e.target.value)}
                      className="flex-1 border border-zinc-300 rounded-md px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                      {(config?.product_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input type="number" min="1" max="10" data-testid={`collection-product-count-${i}`} value={p.count} onChange={(e) => setProduct(i, "count", e.target.value)}
                      className="w-16 border border-zinc-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" title="How many designs of this type" />
                    {products.length > 1 && (
                      <button data-testid={`collection-product-remove-${i}`} onClick={() => removeProduct(i)} className="p-2 rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                    )}
                  </div>
                  <textarea data-testid={`collection-product-desc-${i}`} value={p.description} onChange={(e) => setProduct(i, "description", e.target.value)} rows={2}
                    placeholder="Describe the look / inspiration — e.g. relaxed oversized fit, utility pockets, 90s minimalism (mention a material to render from the description)"
                    className="mt-2 w-full border border-zinc-300 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
                </div>
              ))}
            </div>
            <button data-testid="collection-add-product" onClick={addProduct} className="mt-2 inline-flex items-center gap-1.5 text-sm text-zinc-600 border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100">
              <Plus size={14} /> Add product type
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium">Category</label>
              <select data-testid="collection-category" value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full border border-zinc-300 rounded-md px-2 py-2 text-sm bg-white capitalize focus:outline-none focus:ring-2 focus:ring-zinc-950">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium">Gender</label>
              <select data-testid="collection-gender" value={gender} onChange={(e) => setGender(e.target.value)} className="mt-1 w-full border border-zinc-300 rounded-md px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Any</option>
                {(config?.genders || []).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium">Age group</label>
              <select data-testid="collection-age" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} className="mt-1 w-full border border-zinc-300 rounded-md px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Any</option>
                {(config?.age_groups || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Examples per design</label>
            <div className="mt-2 flex gap-2">
              {[1, 2, 3, 4].map((n) => (
                <button key={n} data-testid={`collection-examples-${n}`} onClick={() => setExamples(n)}
                  className={`w-10 h-10 rounded-md text-sm border transition-colors ${examples === n ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>{n}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Material source</label>
            <div className="mt-2 space-y-1.5">
              {SOURCES.map((s) => (
                <button key={s.key} data-testid={`collection-source-${s.key}`} onClick={() => setMaterialSource(s.key)}
                  className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${materialSource === s.key ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>
                  <span className="text-sm font-medium">{s.label}</span>
                  <span className={`block text-[11px] ${materialSource === s.key ? "text-zinc-300" : "text-zinc-400"}`}>{s.hint}</span>
                </button>
              ))}
            </div>
            {materialSource === "pool" && <MaterialPool value={pool} onChange={setPool} />}
          </div>

          <StyleSelect value={styleId} onChange={setStyleId} />

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Budget cap (USD)</label>
            <input type="number" min="0" step="0.5" data-testid="collection-budget" value={budget} onChange={(e) => setBudget(e.target.value)}
              className="mt-2 w-full border border-zinc-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            <div className="mt-2 text-[11px] text-zinc-500 bg-zinc-100 rounded-md p-2.5 leading-relaxed" data-testid="collection-budget-summary">
              Each render ≈ <b>${costPer.toFixed(2)}</b>. Planned <b>{planned}</b> render{planned !== 1 ? "s" : ""} → will generate <b>{willGenerate}</b> for ~<b>${estCost}</b>
              {skipped > 0 && <span className="text-amber-700"> · {skipped} skipped to stay under budget (cap {maxRenders}/run)</span>}
            </div>
          </div>

          <button data-testid="collection-generate" onClick={generate} disabled={!canGenerate}
            className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 disabled:opacity-40">
            {generating ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Designing collection…</> : <><Sparkles size={16} /> Generate collection</>}
          </button>
        </div>

        {/* ---- Results / review ---- */}
        <div className="lg:col-span-3">
          {!result && !generating && (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg h-full min-h-[400px] flex flex-col items-center justify-center text-center text-zinc-400 p-10" data-testid="collection-empty">
              <Sparkles size={28} className="mb-3" />
              <p className="text-sm max-w-xs">Your generated collection will appear here. Pick what you love, discard the rest, and save them to a season or your Ideas folder.</p>
            </div>
          )}
          {generating && (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg h-full min-h-[400px] flex flex-col items-center justify-center text-center text-zinc-400 p-10">
              <span className="w-7 h-7 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mb-4" />
              <p className="text-sm">Designing your collection — generating {willGenerate} concepts…</p>
            </div>
          )}
          {result && designs.length > 0 && (
            <div data-testid="collection-results">
              <div className="flex items-center justify-between flex-wrap gap-2 sticky top-0 bg-zinc-50/80 backdrop-blur py-2 z-10">
                <div className="flex items-center gap-2">
                  <button data-testid="collection-select-all" onClick={toggleAll} className="text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100">{allSelected ? "Clear" : "Select all"}</button>
                  <span className="text-xs text-zinc-500">{selectedIds.length} selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="collection-save-ideas" onClick={saveToIdeas} disabled={selectedIds.length === 0}
                    className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 disabled:opacity-40"><Lightbulb size={13} /> Save to Ideas</button>
                  <button data-testid="collection-save-collection" onClick={() => { if (selectedIds.length === 0) { toast.error("Select some designs first"); return; } setShowAdd(true); }} disabled={selectedIds.length === 0}
                    className="inline-flex items-center gap-1.5 text-xs bg-zinc-950 text-white rounded-md px-3 py-1.5 hover:bg-zinc-800 disabled:opacity-40"><FolderPlus size={13} /> Add to collection</button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-4">
                {designs.map((d) => (
                  <div key={d.id} data-testid={`collection-card-${d.id}`}
                    className={`relative border rounded-lg overflow-hidden bg-white transition-all ${selected[d.id] ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200"}`}>
                    <button data-testid={`collection-select-${d.id}`} onClick={() => toggleSel(d.id)} className="block w-full text-left">
                      <div className="relative">
                        <img src={imgUrl(d.cover_image)} alt={d.title} className="w-full h-44 object-cover" />
                        <span className={`absolute top-2 left-2 rounded-full p-1 ${selected[d.id] ? "bg-zinc-950 text-white" : "bg-white/80 text-zinc-400 border border-zinc-300"}`}><Check size={12} /></span>
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-medium truncate">{d.title}</p>
                        <p className="text-[10px] text-zinc-400 mt-0.5 truncate">{d.product_type}</p>
                      </div>
                    </button>
                    <button data-testid={`collection-discard-${d.id}`} onClick={() => discard(d.id)} title="Discard"
                      className="absolute top-2 right-2 bg-white/80 hover:bg-red-50 text-zinc-400 hover:text-red-600 rounded-full p-1 border border-zinc-200"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result && designs.length === 0 && (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg min-h-[300px] flex items-center justify-center text-sm text-zinc-400 p-10">All concepts discarded. Generate again to explore more.</div>
          )}
        </div>
      </div>

      {showAdd && <AddToCollectionModal count={selectedIds.length} onClose={() => setShowAdd(false)} onDone={addToCollection} />}
    </div>
  );
}
